import { useEffect, useMemo, useState } from 'react'
import { Tag } from 'antd'
import { adminApi } from '../../api'
import type { KnowledgeScope } from '../../constants/knowledgeScope'
import { folderPathLabel, type FolderLabel } from '../knowledgeFolderUtils'
import { BrowseField } from './BrowseField'
import type { FolderDimensionOption } from './DimensionMultiBrowsePanel'
import { FolderMultiBrowsePanel } from './FolderMultiBrowsePanel'

export type FolderPlacement = {
  folder_id: number
  folder_path?: string
  dimension_id?: number | null
  dimension_name?: string
}

type Props = {
  value?: number[]
  onChange?: (folderIds: number[]) => void
  scope: KnowledgeScope
  dimensions: FolderDimensionOption[]
  dimensionIds?: number[]
  placements?: FolderPlacement[]
  placeholder?: string
  disabled?: boolean
  className?: string
  trigger?: 'hover' | 'click'
  block?: boolean
  variant?: 'inline' | 'input'
}

const EMPTY_IDS: number[] = []
const EMPTY_PLACEMENTS: FolderPlacement[] = []

function folderDisplayLabel(
  folderId: number,
  placements: FolderPlacement[],
  labelsById: Map<number, FolderLabel>,
): string {
  const placement = placements.find((p) => p.folder_id === folderId)
  if (placement) {
    const prefix = placement.dimension_name ? `${placement.dimension_name} / ` : ''
    return `${prefix}${placement.folder_path || placement.folder_id}`
  }
  const path = folderPathLabel(Array.from(labelsById.values()), folderId)
  return path || `#${folderId}`
}

export function KnowledgeFolderMultiBrowseField({
  value,
  onChange,
  scope,
  dimensions,
  dimensionIds,
  placements,
  placeholder = '请选择所属文件夹',
  disabled,
  className,
  trigger = 'click',
  block = false,
  variant = 'inline',
}: Props) {
  const ids = value ?? EMPTY_IDS
  const placementList = placements ?? EMPTY_PLACEMENTS
  const [labelsById, setLabelsById] = useState<Map<number, FolderLabel>>(new Map())

  const idsKey = ids.join(',')
  const dimensionIdsKey = useMemo(() => {
    const merged = new Set<number>()
    for (const id of dimensionIds ?? EMPTY_IDS) {
      if (id > 0) merged.add(id)
    }
    for (const p of placementList) {
      if (p.dimension_id && p.dimension_id > 0) merged.add(p.dimension_id)
    }
    return Array.from(merged)
      .sort((a, b) => a - b)
      .join(',')
  }, [dimensionIds, placementList])

  useEffect(() => {
    const missingIds = ids.filter(
      (id) => !placementList.some((p) => p.folder_id === id && (p.folder_path || p.dimension_name)),
    )
    if (!missingIds.length || !dimensionIdsKey) {
      setLabelsById(new Map())
      return
    }
    const dimIds = dimensionIdsKey.split(',').map(Number).filter((id) => id > 0)
    let cancelled = false
    void (async () => {
      const next = new Map<number, FolderLabel>()
      const need = new Set(missingIds)
      for (const dimId of dimIds) {
        try {
          const r = await adminApi.get<{ items: FolderLabel[] }>('/admin/knowledge/folders/labels', {
            params: { scope, dimension_id: dimId },
          })
          if (cancelled) return
          for (const f of r.data.items || []) {
            if (need.has(f.id)) next.set(f.id, f)
          }
        } catch {
          /* ignore */
        }
      }
      if (!cancelled) setLabelsById(next)
    })()
    return () => {
      cancelled = true
    }
  }, [scope, idsKey, dimensionIdsKey, placementList])

  return (
    <BrowseField
      placeholder={placeholder}
      hidePlaceholder={variant !== 'input' && ids.length > 0}
      disabled={disabled}
      className={className}
      trigger={trigger}
      block={block}
      variant={variant}
      panelClassName="app-browse-popover-panel--folder-multi"
      panel={
        <FolderMultiBrowsePanel
          scope={scope}
          dimensions={dimensions}
          dimensionIds={dimensionIds}
          value={ids}
          onChange={onChange}
        />
      }
    >
      {ids.length ? (
        <span className="app-browse-field-tags">
          {ids.map((id) => (
            <Tag key={id} title={folderDisplayLabel(id, placementList, labelsById)}>
              {folderDisplayLabel(id, placementList, labelsById)}
            </Tag>
          ))}
        </span>
      ) : null}
    </BrowseField>
  )
}
